import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  InlineStack,
  BlockStack,
  Text,
  Badge,
  Button,
  ButtonGroup,
  Divider,
  Modal,
  Form,
  FormLayout,
  TextField,
  Select,
  TextContainer,
  DisplayText,
  List,
  Toast,
  Frame,
  SkeletonPage,
  SkeletonBodyText,
  SkeletonDisplayText,
  Icon,
  Tooltip,
  Banner,
  DataTable,
  Checkbox
} from '@shopify/polaris';
// Note: Icon imports removed as page actions are managed by App.js

// Import services and constants
import { ordersService } from '../services/ordersService';
import { generationsService } from '../services/generationsService';
import { authService } from '../services/authService';
import ManualWhatsAppButton from '../components/buttons/ManualWhatsAppButton';
import ManualTrialRegenerationButton from '../components/buttons/ManualTrialRegenerationButton';
import ViewGenerationModal from '../components/modals/ViewGenerationModal';
import KidImageSelectionModal from '../components/modals/KidImageSelectionModal';
import OrderHelpModal from '../components/modals/OrderHelpModal';
import AddKidPhotosModal from '../components/modals/AddKidPhotosModal';
import EditShippingAddressModal from '../components/modals/EditShippingAddressModal';
import {
  ORDER_STATUS,
  OFFLINE_ORDER_STATUS,
  ORDER_STATUS_LABELS,
  ORDER_STATUS_COLORS,
  ORDER_PRIORITY_LABELS,
  ORDER_PRIORITY_COLORS,
  FLAG_TYPE_LABELS,
  FLAG_TYPE_COLORS,
  orderUtils
} from '../constants/orderConstants';
import { useOrderWebSocket } from '../hooks/useWebSocket';
import {
  getMissingSceneOrders,
  isMissingSceneRecoveryRunning,
} from '../utils/generationRecovery';
import {
  getAddonLabels,
  isAddonLine,
} from '../utils/orderAddons';
import OrderItemEditModal from '../components/modals/OrderItemEditModal';
import RecordPaymentModal from '../components/modals/RecordPaymentModal';
import PaymentBalanceSection from '../components/orders/PaymentBalanceSection';
import PaymentDiscountFields, { getDiscountInput } from '../components/orders/PaymentDiscountFields';
import { useOrderEditPreview } from '../hooks/useOrderEditPreview';
import { buildDiscountOnlyState } from '../utils/orderEditDraft';
import EditOrderButton from '../components/orders/edit/EditOrderButton';
import OrderEditMode from '../components/orders/edit/OrderEditMode';
import {
  useOrderItemEdit,
  useStoryCatalog,
  buildEditToast,
  buildFullEditToast,
} from '../components/orders/OrderItemEditActions';
import { getEditCapabilities } from '../utils/orderEditRules';
import { getLineItemGeneration } from '../utils/orderLineItemGeneration';
import { getStorySkuForDisplay } from '../utils/orderLineSku';
import {
  getPaymentDisplay,
  normalizePaymentStatus
} from '../utils/paymentStatus';
import {
  ViewMajor,
  RefreshMajor
} from '@shopify/polaris-icons';

const makeDiscountRequestId = () => (
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `edit-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
);

// Helper functions to extract data from shopify_properties
const extractKidName = (shopifyProperties) => {
  if (!shopifyProperties || !Array.isArray(shopifyProperties)) return 'N/A';

  const kidNameProperty = shopifyProperties.find(prop =>
    prop.name && prop.name.toLowerCase().includes('kid name')
  );

  return kidNameProperty ? kidNameProperty.value : 'N/A';
};

const extractKidPhotos = (shopifyProperties) => {
  if (!shopifyProperties || !Array.isArray(shopifyProperties)) return [];

  const photoProperties = shopifyProperties.filter(prop =>
    prop.name && prop.name.toLowerCase().includes('photo')
  );
  return photoProperties.map(prop => ({
    name: prop.name,
    value: prop.value
  }));
};

const extractGiftNote = (shopifyProperties) => {
  if (!shopifyProperties || !Array.isArray(shopifyProperties)) return null;

  const noteProperty = shopifyProperties.find(prop =>
    prop.name && (prop.name.toLowerCase() === 'gift message')
  );

  return noteProperty ? noteProperty.value : null;
};

const extractTrialOutput = (lineItem) => {
  // Check if trial_output exists in the line_item
  if (lineItem && lineItem.trial_output) {
    return lineItem.trial_output;
  }
  return null;
};

const renderTrialOutput = (trialOutputUrl, onImageClick) => {
  if (!trialOutputUrl) {
    return <Badge status="attention">Pending</Badge>;
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <img
        src={trialOutputUrl}
        alt="Trial Output"
        style={{
          width: '60px',
          height: '60px',
          objectFit: 'cover',
          borderRadius: '4px',
          cursor: 'pointer',
          border: '1px solid #e1e3e5'
        }}
        onClick={() => onImageClick(trialOutputUrl, 'Trial Output')}
        onError={(e) => {
          e.target.style.display = 'none';
          e.target.nextSibling.style.display = 'inline';
        }}
      />
      <Badge status="success" style={{ display: 'none' }}>Available</Badge>
    </div>
  );
};

const renderKidPhotos = (photos, onImageClick, lineItem, onAddPhotosClick) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {/* Photos Grid */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', maxWidth: '200px' }}>
        {(!photos || photos.length === 0) ? (
          <Text variant="bodySm" tone="subdued">No photos</Text>
        ) : (
          photos.map((photo, index) => {
            // Check if the value looks like a URL
            const isUrl = photo.value && (
              photo.value.startsWith('http') ||
              photo.value.startsWith('https') ||
              photo.value.includes('.')
            );

            if (isUrl) {
              return (
                <div key={index} style={{ position: 'relative' }}>
                  <img
                    src={photo.value}
                    alt={photo.name}
                    style={{
                      width: '60px',
                      height: '60px',
                      objectFit: 'cover',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      border: '1px solid #e1e3e5'
                    }}
                    onClick={() => onImageClick(photo.value, photo.name)}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                  <div
                    style={{
                      display: 'none',
                      fontSize: '10px',
                      padding: '4px',
                      backgroundColor: '#f6f6f7',
                      borderRadius: '4px',
                      width: '60px',
                      height: '60px',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center'
                    }}
                  >
                    <strong>{photo.name}</strong><br />
                    {photo.value}
                  </div>
                </div>
              );
            } else {
              return (
                <div key={index} style={{
                  fontSize: '10px',
                  padding: '4px',
                  backgroundColor: '#f6f6f7',
                  borderRadius: '4px',
                  maxWidth: '60px'
                }}>
                  <strong>{photo.name}:</strong> {photo.value}
                </div>
              );
            }
          })
        )}
      </div>

      {/* Manage Photos Button */}
      <Button
        size="slim"
        onClick={() => onAddPhotosClick(lineItem)}
        variant="plain"
      >
        Manage Photos
      </Button>
    </div>
  );
};

const getGenerationIssue = (generation, order) => {
  if (!generation) return null;

  const generationId = String(generation.generation_id || '');
  const legacyStatus = String(generation.status || '').toLowerCase();
  const localFlags = [
    ...(Array.isArray(generation.flags) ? generation.flags : []),
    ...(Array.isArray(generation.generation_flags) ? generation.generation_flags : [])
  ].filter(flag => {
    if (flag?.resolved) return false;
    const flagGenerationId = flag?.generation_id || flag?.metadata?.generation_id;
    return !flagGenerationId || String(flagGenerationId) === generationId;
  });
  const orderFlags = [
    ...(Array.isArray(generation.order_flags) ? generation.order_flags : []),
    ...(Array.isArray(order?.flags) ? order.flags : [])
  ].filter(flag => {
    if (flag?.resolved) return false;
    const flagGenerationId = flag?.generation_id || flag?.metadata?.generation_id;
    return flagGenerationId && String(flagGenerationId) === generationId;
  });
  const flags = [...localFlags, ...orderFlags];

  const helpFlag = flags.find(flag =>
    ['help', 'help_request'].includes(String(flag?.flag_type || flag?.type || '').toLowerCase())
  );
  if (helpFlag) {
    return {
      type: 'help',
      reason: helpFlag.description || helpFlag.notes || 'Help requested'
    };
  }

  const stuckFlag = flags.find(flag =>
    ['stuck', 'generation_stuck', 'technical_problem'].includes(
      String(flag?.flag_type || flag?.type || '').toLowerCase()
    )
  );
  if (stuckFlag || generation.is_stuck || generation.stuck_reason) {
    return {
      type: 'stuck',
      reason: stuckFlag?.description || stuckFlag?.notes || generation.stuck_reason || 'Generation needs attention'
    };
  }

  if (legacyStatus === 'help' || legacyStatus === 'stuck') {
    return {
      type: legacyStatus,
      reason: legacyStatus === 'help' ? 'Help requested' : 'Generation needs attention'
    };
  }

  return null;
};

const normalizeGenerationStatus = (status) => {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'reprinting') return 'approved';
  if (normalized === 'stuck' || normalized === 'help') return 'pending';
  return normalized;
};

const renderGenerationStatus = (item, generation, isLoading, order) => {
  // If no generation_id, show "Not Started"
  if (!item.active_generation_id && !item.generation_id) {
    return <Badge status="subdued">Not Started</Badge>;
  }

  // If loading, show spinner
  if (isLoading) {
    return <Badge status="info">Loading...</Badge>;
  }

  // If generation not found or failed to load
  if (!generation) {
    return <Badge status="warning">Unknown</Badge>;
  }

  // Map generation status to badge
  const status = normalizeGenerationStatus(generation.status);
  const generationIssue = getGenerationIssue(generation, order);
  const missingSceneOrders = getMissingSceneOrders(generation);
  const recoveryRunning = isMissingSceneRecoveryRunning(generation);

  if (recoveryRunning) {
    return (
      <BlockStack gap="100">
        <Badge status="attention">Recovering</Badge>
        {missingSceneOrders.length > 0 && (
          <Text variant="bodySm" tone="subdued">
            Missing: {missingSceneOrders.join(', ')}
          </Text>
        )}
      </BlockStack>
    );
  }

  if (status === 'pending' && generationIssue) {
    return (
      <BlockStack gap="100">
        <Tooltip content={generationIssue.reason}>
          <Badge status="critical">
            {generationIssue.type === 'help' ? 'Help Needed' : 'Stuck'}
          </Badge>
        </Tooltip>
        {missingSceneOrders.length > 0 && (
          <Text variant="bodySm" tone="subdued">
            Missing: {missingSceneOrders.join(', ')}
          </Text>
        )}
      </BlockStack>
    );
  }

  switch (status) {
    case 'new':
      return <Badge status="info">New</Badge>;
    case 'face_generating':
      return <Badge status="info">Face Generating</Badge>;
    case 'face_pending':
      return <Badge status="info">Face Pending</Badge>;
    case 'trial':
      return <Badge status="warning">Trial</Badge>;
    case 'pending':
      return (
        <BlockStack gap="100">
          <Badge status="attention">Pending</Badge>
          {missingSceneOrders.length > 0 && (
            <Text variant="bodySm" tone="subdued">
              Missing: {missingSceneOrders.join(', ')}
            </Text>
          )}
        </BlockStack>
      );
    case 'completed':
      return <Badge status="success">Completed</Badge>;
    case 'approved':
      return <Badge status="success">Approved</Badge>;
    case 'out_for_printing':
      return <Badge status="info">Out for Printing</Badge>;
    case 'printed':
      return <Badge status="success">Printed</Badge>;
    case 'cancelled':
      return <Badge status="critical">Cancelled</Badge>;
    default:
      return <Badge>{generation.status || 'Unknown'}</Badge>;
  }
};

const OrderDetailPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentUser = authService.getUser();
  const canManageOrders = currentUser?.permissions?.includes('manage_orders');
  const canIssueRefund = Boolean(
    currentUser?.permissions?.includes('refund_orders')
    || currentUser?.permissions?.includes('manage_orders')
    || currentUser?.permissions?.includes('*')
    || currentUser?.group === 'Super Admin'
    || currentUser?.role === 'Super Admin'
  );

  // State management
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [toast, setToast] = useState(null);

  // Modal states
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [commentModalOpen, setCommentModalOpen] = useState(false);
  const [flagModalOpen, setFlagModalOpen] = useState(false);
  const [imagePreviewModalOpen, setImagePreviewModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState({ url: '', name: '' });
  const [resolveFlagModalOpen, setResolveFlagModalOpen] = useState(false);
  const [selectedFlag, setSelectedFlag] = useState(null);
  const [showResolvedFlags, setShowResolvedFlags] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [recordPaymentOpen, setRecordPaymentOpen] = useState(false);
  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [refunds, setRefunds] = useState([]);
  const [refundSubmitting, setRefundSubmitting] = useState(false);
  const [refundSubmissionKey, setRefundSubmissionKey] = useState('');

  // Generation-related states
  const [isViewGenerationModalOpen, setIsViewGenerationModalOpen] = useState(false);
  const [selectedGeneration, setSelectedGeneration] = useState(null);
  const [regeneratingTextById, setRegeneratingTextById] = useState({});
  const [regeneratingMissingById, setRegeneratingMissingById] = useState({});
  const [deletingById, setDeletingById] = useState({});
  const [isKidImageSelectionModalOpen, setIsKidImageSelectionModalOpen] = useState(false);
  const [selectedGenerationForKidImages, setSelectedGenerationForKidImages] = useState(null);
  const [isOrderHelpModalOpen, setIsOrderHelpModalOpen] = useState(false);
  const [selectedGenerationForHelp, setSelectedGenerationForHelp] = useState(null);
  const [isEditShippingAddressModalOpen, setIsEditShippingAddressModalOpen] = useState(false);
  const [isAddKidPhotosModalOpen, setIsAddKidPhotosModalOpen] = useState(false);
  const [selectedLineItemForPhotos, setSelectedLineItemForPhotos] = useState(null);
  const [generatingLineItemById, setGeneratingLineItemById] = useState({});

  // Addon modal states
  const [isAddonsModalOpen, setIsAddonsModalOpen] = useState(false);
  const [addonsLoading, setAddonsLoading] = useState(false);
  const [addonsForm, setAddonsForm] = useState({
    express: false,
    giftLineItemIds: [],
    giftNotes: {},
    newPrice: '',
  });

  // Form states
  const [statusForm, setStatusForm] = useState({ status: '' });
  const [commentForm, setCommentForm] = useState({
    text: '',
    internal: false
  });
  const [flagForm, setFlagForm] = useState({
    type: '',
    description: '',
    priority: 'normal'
  });
  const [paymentForm, setPaymentForm] = useState({
    method: '',
    status: '',
    syncToShopify: true
  });
  const [refundForm, setRefundForm] = useState({
    amount: '',
    refund_date: new Date().toISOString().slice(0, 10),
    reason: '',
    method: '',
    reference: '',
    notes: ''
  });
  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  const [mergeForm, setMergeForm] = useState({
    sourceOrderId: '',
    confirm: false
  });

  const [activeShippingProvider, setActiveShippingProvider] = useState('bosta');

  // Image preview handler
  const handleImageClick = (imageUrl, imageName) => {
    setSelectedImage({ url: imageUrl, name: imageName });
    setImagePreviewModalOpen(true);
  };

  // Add photos handler
  const handleAddPhotosClick = useCallback((lineItem) => {
    setSelectedLineItemForPhotos(lineItem);
    setIsAddKidPhotosModalOpen(true);
  }, []);

  // Handle photos added callback
  const handlePhotosAdded = useCallback((updatedOrder) => {
    // Refresh the order to show new photos
    loadOrder();
  }, []);

  // WebSocket integration for real-time order updates
  const {
    isConnected,
    connectionError,
    orderData: wsOrderData,
    lastUpdate
  } = useOrderWebSocket(orderId, (data) => {
    // Handle real-time updates
    if (data.type === 'orderUpdated' && data.order) {
      setOrder(prevOrder => ({ ...prevOrder, ...data.order }));
      setToast({
        content: 'Order updated in real-time',
        duration: 3000
      });
    } else if (data.type === 'orderStatusChanged') {
      setOrder(prevOrder => ({
        ...prevOrder,
        status: data.new_status,
        updated_at: data.timestamp
      }));
      setToast({
        content: `Order status changed to ${orderUtils.getStatusLabel(data.new_status)}`,
        duration: 3000
      });
    } else if (data.type === 'orderCommentAdded') {
      setOrder(prevOrder => ({
        ...prevOrder,
        comments: [...(prevOrder.comments || []), data.comment],
        updated_at: data.timestamp
      }));
      setToast({
        content: 'New comment added',
        duration: 3000
      });
    } else if (data.type === 'orderFlagAdded') {
      setOrder(prevOrder => ({
        ...prevOrder,
        flags: [...(prevOrder.flags || []), data.flag],
        updated_at: data.timestamp
      }));
      setToast({
        content: `New ${data.flag.type} flag added`,
        duration: 3000
      });
    }
  });

  // Load order details
  const editingOrderRef = useRef(false);
  const loadOrder = useCallback(async (options) => {
    try {
      // Showing the skeleton would unmount edit mode and lose the staged draft.
      if (options?.silent !== true && !editingOrderRef.current) setLoading(true);
      const requestedCountry = String(
        searchParams.get('country') || authService.getActiveCountry() || ''
      ).trim().toUpperCase();
      if (!authService.getAllowedCountries().includes(requestedCountry)) {
        setToast({
          content: 'This order country is not authorized.',
          error: true,
        });
        navigate(`/orders?country=${encodeURIComponent(
          authService.getActiveCountry()
        )}`, { replace: true });
        return;
      }
      authService.setActiveCountry(requestedCountry);
      if (!searchParams.get('country')) {
        navigate(
          `/orders/${encodeURIComponent(orderId)}?country=${requestedCountry}`,
          { replace: true }
        );
      }
      const detail = await ordersService.getOrderDetail(
        orderId,
        requestedCountry,
      );
      const orderData = detail?.order;

      if (!orderData) {
        setToast({
          content: 'Order not found',
          error: true
        });
        navigate('/orders');
        return;
      }

      setOrder(orderData);
      setStatusForm(prev => ({ ...prev, status: orderData.status }));

      // Store order detail data in localStorage for App.js subtitle
      const formattedOrder = ordersService.formatOrderForDisplay(orderData);
      localStorage.setItem('orderDetailData', JSON.stringify({
        status: orderUtils.getStatusLabel(orderData.status),
        createdAt: formattedOrder.formattedCreatedAt
      }));

    } catch (error) {
      console.error('Error loading order:', error);
      setToast({
        content: `Failed to load order: ${error.message}`,
        error: true
      });
    } finally {
      setLoading(false);
    }
  }, [orderId, navigate, searchParams]);

  const [editingOrder, setEditingOrder] = useState(false);
  editingOrderRef.current = editingOrder;
  const storyCatalog = useStoryCatalog();
  const handleOrderItemSaved = useCallback((result, mode) => {
    setToast({ content: buildEditToast(result, mode), error: false });
    loadOrder();
  }, [loadOrder]);
  const {
    modalProps: orderItemEditModalProps,
    openAdd: openAddStory,
    catalogError: storyCatalogError,
  } = useOrderItemEdit({ order, onSaved: handleOrderItemSaved, storyCatalog });

  const handleOpenEditMode = useCallback(async () => {
    if (await storyCatalog.loadCatalog()) setEditingOrder(true);
  }, [storyCatalog]);
  const handleOrderEditSaved = useCallback(result => {
    setToast({ content: buildFullEditToast(result), error: false });
    loadOrder();
  }, [loadOrder]);
  const handleReloadOrder = useCallback(() => loadOrder({ silent: true }), [loadOrder]);

  useEffect(() => {
    if (storyCatalogError) {
      setToast({
        content: `Failed to load story catalog: ${storyCatalogError.message}`,
        error: true,
      });
    }
  }, [storyCatalogError]);

  const loadRefunds = useCallback(async () => {
    try {
      setRefunds(await ordersService.getOrderRefunds(orderId));
    } catch (error) {
      console.error('Error loading refunds:', error);
      setRefunds([]);
    }
  }, [orderId]);

  const loadShippingProviderSetting = useCallback(async () => {
    try {
      const settings = await ordersService.getAdminSettings();
      const provider = settings?.shipping?.provider || 'bosta';
      setActiveShippingProvider(provider);
    } catch (error) {
      console.error('Error loading shipping provider setting:', error);
      setActiveShippingProvider('bosta');
    }
  }, []);

  // Handle AWB / Label download - provider-aware
  const handleDownloadBostaAWB = useCallback(async () => {
    if (!order) return;

    try {
      setUpdating(true);

      if (activeShippingProvider === 'shipping_company') {
        // Download Shipping Company label (PNG image)
        await ordersService.downloadShippingCompanyLabel(order.order_id);
        setToast({
          content: 'Shipping label downloaded successfully',
          error: false
        });
      } else {
        // Download Bosta AWB (PDF)
        await ordersService.downloadBostaAWB(order.order_id, {
          requestedAwbType: 'A4',
          lang: 'ar'
        });
        setToast({
          content: 'AWB downloaded successfully',
          error: false
        });
      }
    } catch (error) {
      setToast({
        content: `Failed to download label: ${error.message}`,
        error: true
      });
    } finally {
      setUpdating(false);
    }
  }, [order, activeShippingProvider]);

  // Handle Recreate Bosta Order
  const handleRecreateBostaOrder = useCallback(async () => {
    if (!order) return;
    const providerLabel = activeShippingProvider === 'shipping_company' ? 'Shipping Company' : 'Bosta';
    if (!window.confirm(`Are you sure you want to recreate the ${providerLabel} order? This will generate a new delivery and tracking number.`)) {
      return;
    }

    try {
      setUpdating(true);
      const result = await ordersService.recreateBostaOrder(order.order_id);
      const resultProvider = result?.provider || activeShippingProvider;

      setToast({
        content: `${resultProvider === 'shipping_company' ? 'Shipping Company' : 'Bosta'} order recreated successfully. Tracking number updated.`,
        error: false
      });

      if (resultProvider === 'shipping_company') {
        const labelUrl = result?.label_url || result?.shipping_app?.label_url;
        if (labelUrl) {
          window.open(labelUrl, '_blank');
        }
      } else if (result && result.data && result.data.awb_url) {
        window.open(result.data.awb_url, '_blank');
      }

      loadOrder();
    } catch (error) {
      setToast({
        content: `Failed to recreate shipping order: ${error.message}`,
        error: true
      });
    } finally {
      setUpdating(false);
    }
  }, [order, loadOrder, activeShippingProvider]);

  // Load data on component mount
  useEffect(() => {
    loadOrder();
    loadRefunds();
    if (canManageOrders) {
      loadShippingProviderSetting();
    }
  }, [
    canManageOrders,
    loadOrder,
    loadRefunds,
    loadShippingProviderSetting,
  ]);

  // Add event listeners for actions from App.js
  useEffect(() => {
    const handleUpdateStatus = () => setStatusModalOpen(true);
    const handleAddComment = () => setCommentModalOpen(true);
    const handleAddFlag = () => setFlagModalOpen(true);
    const handleRefresh = () => loadOrder();
    const handleDownloadAWB = () => handleDownloadBostaAWB();
    const handleRecreateBosta = () => handleRecreateBostaOrder();
    const handleMergeOrder = () => setMergeModalOpen(true);

    // Add event listeners
    window.addEventListener('orderDetailUpdateStatus', handleUpdateStatus);
    window.addEventListener('orderDetailAddComment', handleAddComment);
    window.addEventListener('orderDetailAddFlag', handleAddFlag);
    window.addEventListener('orderDetailRefresh', handleRefresh);
    window.addEventListener('orderDetailDownloadAWB', handleDownloadAWB);
    window.addEventListener('orderDetailRecreateShippingOrder', handleRecreateBosta);
    window.addEventListener('orderDetailMergeOrder', handleMergeOrder);

    // Cleanup event listeners
    return () => {
      window.removeEventListener('orderDetailUpdateStatus', handleUpdateStatus);
      window.removeEventListener('orderDetailAddComment', handleAddComment);
      window.removeEventListener('orderDetailAddFlag', handleAddFlag);
      window.removeEventListener('orderDetailRefresh', handleRefresh);
      window.removeEventListener('orderDetailDownloadAWB', handleDownloadAWB);
      window.removeEventListener('orderDetailRecreateShippingOrder', handleRecreateBosta);
      window.removeEventListener('orderDetailMergeOrder', handleMergeOrder);
    };
  }, [loadOrder, handleDownloadBostaAWB, handleRecreateBostaOrder]);

  // Handle status update
  const handleStatusUpdate = useCallback(async () => {
    if (!statusForm.status || !order) return;

    try {
      setUpdating(true);

      const statusData = { status: statusForm.status };

      await ordersService.updateOrderStatus(order.order_id, statusData);

      setToast({
        content: 'Order status updated successfully',
        error: false
      });

      setStatusModalOpen(false);
      loadOrder(); // Reload order data
    } catch (error) {
      setToast({
        content: `Failed to update status: ${error.message}`,
        error: true
      });
    } finally {
      setUpdating(false);
    }
  }, [statusForm, order, loadOrder]);

  const handleConfirmOrder = useCallback(async () => {
    if (!order) return;

    if (!window.confirm('Confirm this order and move it to pending generation?')) {
      return;
    }

    try {
      setUpdating(true);
      await ordersService.confirmOrder(order.order_id || order.id);
      setToast({
        content: 'Order confirmed successfully',
        error: false
      });
      loadOrder();
    } catch (error) {
      setToast({
        content: `Failed to confirm order: ${error.message}`,
        error: true
      });
    } finally {
      setUpdating(false);
    }
  }, [order, loadOrder]);

  const handleRecordItemReturn = useCallback(async (item) => {
    if (!order) return;
    const reason = window.prompt('Reason this item needs correction:');
    if (!reason?.trim()) return;

    try {
      setUpdating(true);
      await ordersService.recordItemReturn(
        order.order_id,
        item.id,
        reason.trim()
      );
      setToast({ content: 'Item return recorded', error: false });
      await loadOrder();
    } catch (error) {
      setToast({
        content: `Failed to record item return: ${error.message}`,
        error: true
      });
    } finally {
      setUpdating(false);
    }
  }, [order, loadOrder]);

  const handleCreateItemRedelivery = useCallback(async (item) => {
    if (!order) return;
    const feeInput = window.prompt('Shipping fee to collect for this redelivery:', '0');
    if (feeInput === null) return;
    const shippingFee = Number(feeInput);
    if (!Number.isFinite(shippingFee) || shippingFee < 0) {
      setToast({ content: 'Shipping fee must be a non-negative number', error: true });
      return;
    }
    if (!window.confirm(`Create a ${activeShippingProvider === 'shipping_company' ? 'Shipping Company' : 'Bosta'} redelivery for this item?`)) {
      return;
    }

    try {
      setUpdating(true);
      await ordersService.createItemRedelivery(
        order.order_id,
        item.id,
        shippingFee,
        activeShippingProvider
      );
      setToast({ content: 'Item redelivery created', error: false });
      await loadOrder();
    } catch (error) {
      setToast({
        content: `Failed to create item redelivery: ${error.message}`,
        error: true
      });
    } finally {
      setUpdating(false);
    }
  }, [order, activeShippingProvider, loadOrder]);

  // Handle comment addition
  const handleAddComment = useCallback(async () => {
    if (!commentForm.text.trim() || !order) return;

    try {
      setUpdating(true);

      const commentData = {
        comment: commentForm.text.trim(),
        internal: commentForm.internal
      };

      await ordersService.addComment(order.order_id, commentData);

      setToast({
        content: 'Comment added successfully',
        error: false
      });

      setCommentModalOpen(false);
      setCommentForm({ text: '', internal: false });
      loadOrder(); // Reload order data
    } catch (error) {
      setToast({
        content: `Failed to add comment: ${error.message}`,
        error: true
      });
    } finally {
      setUpdating(false);
    }
  }, [commentForm, order, loadOrder]);

  // Handle flag addition
  const handleAddFlag = useCallback(async () => {
    if (!flagForm.type || !flagForm.description.trim() || !order) return;

    try {
      setUpdating(true);

      const flagData = {
        type: flagForm.type,
        description: flagForm.description.trim(),
        priority: flagForm.priority
      };

      await ordersService.addFlag(order.order_id, flagData);

      setToast({
        content: 'Flag added successfully',
        error: false
      });

      setFlagModalOpen(false);
      setFlagForm({ type: '', description: '', priority: 'normal' });
      loadOrder(); // Reload order data
    } catch (error) {
      setToast({
        content: `Failed to add flag: ${error.message}`,
        error: true
      });
    } finally {
      setUpdating(false);
    }
  }, [flagForm, order, loadOrder]);

  // Handle flag resolution
  const handleResolveFlag = useCallback(async () => {
    if (!selectedFlag || !order) return;

    try {
      setUpdating(true);

      await ordersService.resolveFlag(order.order_id, selectedFlag.flag_id, resolutionNotes.trim() || null);

      setToast({
        content: 'Flag resolved successfully',
        error: false
      });

      setResolveFlagModalOpen(false);
      setSelectedFlag(null);
      setResolutionNotes('');
      loadOrder(); // Reload order data
    } catch (error) {
      setToast({
        content: `Failed to resolve flag: ${error.message}`,
        error: true
      });
    } finally {
      setUpdating(false);
    }
  }, [selectedFlag, order, resolutionNotes, loadOrder]);

  // Open resolve flag modal
  const openResolveFlagModal = useCallback((flag) => {
    setSelectedFlag(flag);
    setResolutionNotes('');
    setResolveFlagModalOpen(true);
  }, []);

  const [paymentDiscount, setPaymentDiscount] = useState({ amount: '', reason: '' });
  const [paymentDiscountError, setPaymentDiscountError] = useState('');
  const discountRequestIdRef = useRef(makeDiscountRequestId());
  const paymentSubmitRef = useRef(false);

  useEffect(() => {
    if (paymentModalOpen) return;
    setPaymentDiscount({ amount: '', reason: '' });
    setPaymentDiscountError('');
    discountRequestIdRef.current = makeDiscountRequestId();
  }, [paymentModalOpen]);

  const paymentCapabilities = getEditCapabilities(order);
  const discountBody = useMemo(() => {
    const basic = getDiscountInput(paymentDiscount, null);
    if (!paymentModalOpen || !order || !paymentCapabilities.canDiscount) return null;
    if (!basic.active || !basic.valid) return null;
    return buildDiscountOnlyState(order, { amount: basic.amount, reason: basic.reason });
  }, [paymentModalOpen, order, paymentDiscount, paymentCapabilities.canDiscount]);
  const {
    preview: discountPreview,
    status: discountStatus,
    error: discountPreviewError
  } = useOrderEditPreview(order?.order_id, discountBody);

  const discountInput = getDiscountInput(paymentDiscount, discountPreview?.max_discount);
  const discountPending = paymentCapabilities.canDiscount
    && paymentDiscount.amount.trim() !== '';
  const discountReady = discountPending
    && discountInput.active
    && discountInput.valid
    && discountStatus === 'ready'
    && (discountPreview?.blocking_errors || []).length === 0;

  const handleDiscountChange = useCallback(next => {
    discountRequestIdRef.current = makeDiscountRequestId();
    setPaymentDiscountError('');
    setPaymentDiscount(next);
  }, []);

  // Handle payment update
  const handleUpdatePayment = useCallback(async () => {
    if (!order || paymentSubmitRef.current) return;
    const hasPaymentChange = Boolean(paymentForm.method || paymentForm.status);
    if (discountPending && !discountReady) return;
    if (!discountPending && !hasPaymentChange) {
      setToast({
        content: 'Please select at least one field to update',
        error: true
      });
      return;
    }

    paymentSubmitRef.current = true;
    setUpdating(true);
    let discountResult = null;
    try {
      if (discountPending) {
        try {
          discountResult = await ordersService.saveOrderEdit(
            order.order_id,
            buildDiscountOnlyState(
              order,
              { amount: discountInput.amount, reason: discountInput.reason },
              discountRequestIdRef.current
            )
          );
        } catch (error) {
          const details = error?.details;
          const detailText = details
            ? (typeof details === 'string' ? details : JSON.stringify(details))
            : '';
          setPaymentDiscountError(
            [error?.message || 'Failed to apply the discount', detailText].filter(Boolean).join(': ')
          );
          return;
        }
        setPaymentDiscount({ amount: '', reason: '' });
        discountRequestIdRef.current = makeDiscountRequestId();
      }

      const discountToast = discountResult
        ? buildFullEditToast(discountResult, 'Discount applied')
        : '';

      if (!hasPaymentChange) {
        setToast({ content: discountToast, error: false });
        setPaymentModalOpen(false);
        loadOrder();
        return;
      }

      try {
        const updateData = { syncToShopify: paymentForm.syncToShopify };
        if (paymentForm.method) updateData.method = paymentForm.method;
        if (paymentForm.status) updateData.status = paymentForm.status;

        const result = await ordersService.updatePaymentInfo(order.order_id, updateData);
        const paymentToast = `Payment updated successfully${result.shopify_synced ? ' and synced to Shopify' : ''}`;
        setToast({
          content: discountToast ? `${discountToast}. ${paymentToast}` : paymentToast,
          error: false
        });
        setPaymentModalOpen(false);
        setPaymentForm({ method: '', status: '', syncToShopify: true });
        loadOrder(); // Reload order data
      } catch (error) {
        setToast({
          content: `${discountToast ? `${discountToast}, but the payment update failed` : 'Failed to update payment'}: ${error.message}`,
          error: true
        });
        if (discountResult) loadOrder();
      }
    } finally {
      paymentSubmitRef.current = false;
      setUpdating(false);
    }
  }, [paymentForm, order, loadOrder, discountPending, discountReady, discountInput.amount, discountInput.reason]);

  const handleIssueRefund = useCallback(async () => {
    if (!order || !refundForm.amount) return;
    setRefundSubmitting(true);
    try {
      const result = await ordersService.issueRefund(order.order_id, {
        ...refundForm,
        amount: Number(refundForm.amount),
        currency: order.payment?.currency || order.currency || 'EGP'
      }, refundSubmissionKey);
      setRefunds(result.refunds || []);
      setRefundModalOpen(false);
      setRefundForm({
        amount: '',
        refund_date: new Date().toISOString().slice(0, 10),
        reason: '',
        method: '',
        reference: '',
        notes: ''
      });
      setToast({ content: 'Refund recorded successfully' });
      await loadOrder();
    } catch (error) {
      setToast({ content: error.message, error: true });
    } finally {
      setRefundSubmitting(false);
    }
  }, [loadOrder, order, refundForm, refundSubmissionKey]);

  // Handle merge orders
  const handleMergeOrders = useCallback(async () => {
    if (!order || !mergeForm.sourceOrderId) return;

    // Check if confirming is required and checked (if we added a checkbox, but simplified here)

    try {
      setUpdating(true);

      await ordersService.mergeOrders(order.order_id, mergeForm.sourceOrderId);

      setToast({
        content: `Order ${mergeForm.sourceOrderId} successfully merged into ${order.order_id}`,
        error: false
      });

      setMergeModalOpen(false);
      setMergeForm({ sourceOrderId: '', confirm: false });
      loadOrder(); // Reload order data

    } catch (error) {
      setToast({
        content: `Failed to merge orders: ${error.message}`,
        error: true
      });
    } finally {
      setUpdating(false);
    }
  }, [mergeForm, order, loadOrder]);

  // Loading state
  if (loading) {
    return (
      <SkeletonPage
        primaryAction
        secondaryActions={2}
        breadcrumbs
      >
        <Layout>
          <Layout.Section oneHalf>
            <Card sectioned>
              <SkeletonDisplayText size="small" />
              <SkeletonBodyText lines={3} />
            </Card>
          </Layout.Section>
          <Layout.Section oneHalf>
            <Card sectioned>
              <SkeletonDisplayText size="small" />
              <SkeletonBodyText lines={3} />
            </Card>
          </Layout.Section>
        </Layout>
      </SkeletonPage>
    );
  }

  if (!order) {
    return (
      <Layout>
        <Layout.Section>
          <Card sectioned>
            <TextContainer>
              <Text>The requested order could not be found.</Text>
            </TextContainer>
          </Card>
        </Layout.Section>
      </Layout>
    );
  }

  const formattedOrder = ordersService.formatOrderForDisplay(order);
  // Order-level generation issue (any unresolved line item flag) feeds the edit
  // capability check so a help-flagged generation still allows a story swap.
  const orderGenerationIssue = (order.line_items || []).reduce((found, item) => {
    if (found) return found;
    return getGenerationIssue(getLineItemGeneration(item), order);
  }, null);
  const sourceDisplay = orderUtils.getSourceDisplay(order);
  const nextStatuses = orderUtils.getNextStatuses(order.status, order.source);
  const paymentDisplay = getPaymentDisplay(order.payment);
  const paymentAmount = Number(order.payment?.amount || order.total_price || 0);
  const refundedAmount = Number(order.payment?.refunded_amount || 0);
  const remainingRefundable = Math.max(0, paymentAmount - refundedAmount);
  // Prepare items table data
  const visibleLineItems = (order.line_items || []).filter(
    item => !isAddonLine(item)
  );
  const itemsTableRows = visibleLineItems.map(item => {
    const kidName = extractKidName(item.properties);
    const kidPhotos = extractKidPhotos(item.properties);
    const giftNote = extractGiftNote(item.properties);
    const addonLabels = getAddonLabels(item);
    // const trialOutput = extractTrialOutput(item);
    const generation = getLineItemGeneration(item);
    const generationStatus = normalizeGenerationStatus(generation?.status);
    const generationIssue = getGenerationIssue(generation, order);
    const missingSceneOrders = getMissingSceneOrders(generation);
    const recoveryRunning = isMissingSceneRecoveryRunning(generation);
    const openItemReturn = (item.returns || []).find(
      itemReturn => itemReturn.status !== 'redelivered'
    );

    // Create action buttons for this specific order item
    const actionButtons = (
      <InlineStack gap="200">
        {/* Trial-related buttons (always show) */}
        <ManualWhatsAppButton
          order={order}
          orderItem={item}
          onWhatsAppSent={(orderId, orderItemId) => {
            loadOrder();
          }}
        />
        <ManualTrialRegenerationButton
          order={order}
          orderItem={item}
          onTrialRegenerated={(orderId, orderItemId) => {
            loadOrder();
          }}
        />

        {/* Generation-related buttons - conditional based on status */}
        {generation && (generationStatus === 'new' || generationIssue?.type === 'help') ? (
          <>
            {/* Actions for NEW generations or pending generations with a help flag. */}
            <Tooltip content="Select Kid Image">
              <Button
                icon={<span style={{ fontWeight: 700, fontSize: 14 }}>📷</span>}
                size="slim"
                tone="success"
              onClick={async () => {
                const fullGeneration = await generationsService
                  .getGenerationByGenerationId(generation.generation_id);
                setSelectedGenerationForKidImages(fullGeneration || generation);
                  setIsKidImageSelectionModalOpen(true);
                }}
              />
            </Tooltip>
          </>
        ) : generation ? (
          <>
            {/* Actions for all OTHER statuses (not new/help) */}
            <Tooltip content="View Generation">
              <Button
                icon={ViewMajor}
                size="slim"
                onClick={() => {
                  setSelectedGeneration(generation);
                  setIsViewGenerationModalOpen(true);
                }}
              />
            </Tooltip>

            {/* Regenerate Text - show for all except 'out_for_printing' and 'printed' */}
            {(generationStatus !== 'out_for_printing' && generationStatus !== 'printed') && (
              <Tooltip content="Regenerate text overlays">
                <Button
                  icon={<span style={{ fontWeight: 700, fontSize: 14 }}>T</span>}
                  size="slim"
                  loading={!!regeneratingTextById[generation.generation_id]}
                  onClick={async () => {
                    const genId = generation.generation_id;
                    if (!genId) return;
                    setRegeneratingTextById(prev => ({ ...prev, [genId]: true }));
                    try {
                      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/generation-stories/regenerate-text-scenes`, {
                        method: 'POST',
                        headers: {
                          ...authService.getAuthHeaders(),
                          'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({ generationId: genId }),
                      });
                      if (!response.ok) throw new Error('Failed to regenerate text overlays');
                      setToast({ content: 'Regenerating text overlays started', error: false });
                      setTimeout(() => loadOrder(), 2000);
                    } catch (error) {
                      setToast({ content: `Error: ${error.message}`, error: true });
                    } finally {
                      setRegeneratingTextById(prev => ({ ...prev, [genId]: false }));
                    }
                  }}
                />
              </Tooltip>
            )}

            {/* Retry Missing Scenes */}
            {missingSceneOrders.length > 0 && ['pending', 'trial', 'completed'].includes(generationStatus) && (
              <Tooltip content={recoveryRunning ? 'Missing-scene recovery is running' : `Regenerate missing scenes: ${missingSceneOrders.join(', ')}`}>
                <Button
                  icon={RefreshMajor}
                  size="slim"
                  disabled={recoveryRunning}
                  loading={recoveryRunning || !!regeneratingMissingById[generation.generation_id]}
                  onClick={async () => {
                    const genId = generation.generation_id;
                    if (!genId) return;
                    setRegeneratingMissingById(prev => ({ ...prev, [genId]: true }));
                    try {
                      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/generation-stories/regenerate-missing`, {
                        method: 'POST',
                        headers: {
                          ...authService.getAuthHeaders(),
                          'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({ generationId: genId }),
                      });
                      let data = {};
                      try { data = await response.clone().json(); } catch { }
                      if (!response.ok) {
                        throw new Error(data?.error || data?.message || 'Failed to recover missing scenes');
                      }
                      setToast({
                        content: `Recovering missing scenes: ${(data?.missingOrders || missingSceneOrders).join(', ')}`,
                        error: false
                      });
                      setTimeout(() => loadOrder(), 2000);
                    } catch (error) {
                      setToast({ content: `Error: ${error.message}`, error: true });
                    } finally {
                      setRegeneratingMissingById(prev => ({ ...prev, [genId]: false }));
                    }
                  }}
                />
              </Tooltip>
            )}

          </>
        ) : null}

        {/* Generate button - show when item requires generation but has no generation_id or status not started */}
        {item.requires_generation
          && (!item.active_generation_id && !item.generation_id)
          && (
          <Tooltip content="Create generation document for this item">
            <Button
              icon={<span style={{ fontWeight: 700, fontSize: 14 }}>⚡</span>}
              size="slim"
              tone="success"
              loading={!!generatingLineItemById[item.id]}
              onClick={async () => {
                if (!window.confirm(`Create generation document for "${item.name || item.title}"?`)) {
                  return;
                }

                setGeneratingLineItemById(prev => ({ ...prev, [item.id]: true }));
                try {
                  const result = await ordersService.generateLineItem(order.order_id, item.id);
                  if (result && result.success) {
                    setToast({
                      content: `Generation created: ${result.generation_id || 'Success'}`,
                      error: false
                    });
                    setTimeout(() => loadOrder(), 1000);
                  } else {
                    throw new Error(result?.error || 'Failed to generate');
                  }
                } catch (error) {
                  setToast({
                    content: `Failed to generate: ${error.message}`,
                    error: true
                  });
                } finally {
                  setGeneratingLineItemById(prev => ({ ...prev, [item.id]: false }));
                }
              }}
            >
              Generate
            </Button>
          </Tooltip>
        )}

        {canManageOrders && order.status === ORDER_STATUS.DELIVERED && (
          openItemReturn?.status === 'returned_for_correction' ? (
            <Button
              size="slim"
              loading={updating}
              onClick={() => handleCreateItemRedelivery(item)}
            >
              Redeliver
            </Button>
          ) : openItemReturn ? (
            <Button size="slim" disabled>
              {openItemReturn.status === 'redelivery_in_transit'
                ? 'Redelivery in transit'
                : 'Redelivery created'}
            </Button>
          ) : (
            <Button
              size="slim"
              loading={updating}
              onClick={() => handleRecordItemReturn(item)}
            >
              Return item
            </Button>
          )
        )}
      </InlineStack>
    );

    return [
      <InlineStack gap="100" align="start" blockAlign="center">
        <Text>{item.name || 'N/A'}</Text>
        {addonLabels.map(label => {
          const badge = (
            <Badge
              key={label}
              status={label === 'Express'
                ? 'critical'
                : label === 'Premium Printing'
                  ? 'attention'
                  : label === 'Gift Box'
                    ? 'success'
                    : 'info'}
              size="small"
            >
              {label}
            </Badge>
          );
          return label === 'Gift Note' ? (
            <Tooltip key={label} content={giftNote || 'No message found'}>
              {badge}
            </Tooltip>
          ) : badge;
        })}
      </InlineStack>,
      getStorySkuForDisplay(item) || 'N/A',
      kidName,
      renderKidPhotos(kidPhotos, handleImageClick, item, handleAddPhotosClick),
      // renderTrialOutput(trialOutput, handleImageClick),
      renderGenerationStatus(item, generation, false, order),
      new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: order.payment?.currency || 'EGP'
      }).format(item.price || 0),
      actionButtons
    ];
  }) || [];

  const itemsTableHeadings = [
    'Product',
    'Item ID',
    'Kid Name',
    'Kid Photos',
    'Generation',
    'Price',
    'Actions',
  ];

  // Prepare comments table data
  const commentsTableRows = order.comments?.map(comment => [
    comment.comment || '',
    ((comment.author?.name || comment.author) ? `${comment.author?.name || comment.author} (${comment.user_role || 'System'})` : (comment.user_role || 'System')),
    'Public', // API doesn't specify internal/public, defaulting to Public
    new Date(comment.timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  ]) || [];

  const commentsTableHeadings = ['Comment', 'Author', 'Type', 'Date'];

  return (
    <>
      {/* WebSocket Connection Status */}
      {/* {connectionError && (
          <Layout.Section>
            <Banner status="warning" title="Connection Issue">
              <p>Real-time updates may be delayed. {connectionError}</p>
            </Banner>
          </Layout.Section>
        )} */}

      <Layout>
        {/* Order Details, Customer Information, and Payment Information in one row */}
        <Layout.Section>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
            {/* Order Information */}
            <Card
              title="Order Information"
              sectioned
            >
              <BlockStack gap="400">
                <InlineStack align="space-between">
                  <Text variant="bodyMd" fontWeight="medium">Order ID:</Text>
                  <Text variant="bodyMd">{order.order_id}</Text>
                </InlineStack>

                <InlineStack align="space-between">
                  <Text variant="bodyMd" fontWeight="medium">Source:</Text>
                  <InlineStack gap="200">
                    <Text variant="bodyMd">{order.source}</Text>
                    <Badge status={sourceDisplay.color} size="small">
                      {sourceDisplay.label}
                    </Badge>
                  </InlineStack>
                </InlineStack>

                <InlineStack align="space-between">
                  <Text variant="bodyMd" fontWeight="medium">Status:</Text>
                  <InlineStack gap="200" blockAlign="center">
                    <Badge status={ORDER_STATUS_COLORS[order.status] || 'subdued'}>
                      {orderUtils.getStatusLabel(order.status)}
                    </Badge>
                    {order.status === ORDER_STATUS.PENDING_CONFIRMATION && canManageOrders && (
                      <Button
                        size="slim"
                        variant="primary"
                        loading={updating}
                        onClick={handleConfirmOrder}
                      >
                        Confirm Order
                      </Button>
                    )}
                  </InlineStack>
                </InlineStack>

                <InlineStack align="space-between">
                  <Text variant="bodyMd" fontWeight="medium">Priority:</Text>
                  <Badge status={ORDER_PRIORITY_COLORS[order.priority]}>
                    {ORDER_PRIORITY_LABELS[order.priority]}
                  </Badge>
                </InlineStack>

                <InlineStack align="space-between">
                  <Text variant="bodyMd" fontWeight="medium">Total:</Text>
                  <Text variant="bodyMd" fontWeight="medium">
                    {formattedOrder.formattedTotal}
                  </Text>
                </InlineStack>

                <InlineStack align="space-between">
                  <Text variant="bodyMd" fontWeight="medium">Created:</Text>
                  <Text variant="bodyMd">{formattedOrder.formattedCreatedAt}</Text>
                </InlineStack>

                <InlineStack align="space-between">
                  <Text variant="bodyMd" fontWeight="medium">Updated:</Text>
                  <Text variant="bodyMd">{formattedOrder.formattedUpdatedAt}</Text>
                </InlineStack>

              </BlockStack>
            </Card>

            {/* Customer Information */}
            <Card title="Customer Information" sectioned>
              <BlockStack gap="400">
                <InlineStack align="space-between">
                  <Text variant="bodyMd" fontWeight="medium">Name:</Text>
                  <Text variant="bodyMd">{order.customer?.name || 'N/A'}</Text>
                </InlineStack>

                <InlineStack align="space-between">
                  <Text variant="bodyMd" fontWeight="medium">Email:</Text>
                  <Text variant="bodyMd">{order.customer?.email || 'N/A'}</Text>
                </InlineStack>

                <InlineStack align="space-between">
                  <Text variant="bodyMd" fontWeight="medium">Phone:</Text>
                  <Text variant="bodyMd">{order.customer?.phone || 'N/A'}</Text>
                </InlineStack>

                {order.shipping_address && (
                  <>
                    <Divider />
                    <InlineStack align="space-between" blockAlign="center">
                      <Text variant="headingMd">Shipping Address</Text>
                      <Button
                        variant="plain"
                        onClick={() => setIsEditShippingAddressModalOpen(true)}
                      >
                        Edit
                      </Button>
                    </InlineStack>
                    <Text variant="bodyMd">
                      {order.shipping_address.address1}<br />
                      {order.shipping_address.city}, {order.shipping_address.province} {order.shipping_address.postal_code}<br />
                      {order.shipping_address.country}
                    </Text>
                  </>
                )}
              </BlockStack>
            </Card>

            {/* Payment Information */}
            <Card sectioned>
              <BlockStack gap="400">
                <InlineStack align="space-between" blockAlign="center">
                  <Text variant="headingMd" as="h2">Payment Information</Text>
                  <ButtonGroup>
                    {canIssueRefund
                      && remainingRefundable > 0
                      && ['paid', 'collected', 'refunded'].includes(
                        normalizePaymentStatus(order.payment?.status)
                      ) && (
                      <Button
                        onClick={() => {
                          setRefundForm({
                            amount: '',
                            refund_date: new Date().toISOString().slice(0, 10),
                            reason: '',
                            method: '',
                            reference: '',
                            notes: ''
                          });
                          setRefundSubmissionKey(
                            `refund-${Date.now()}-${Math.random().toString(36).slice(2)}`
                          );
                          setRefundModalOpen(true);
                        }}
                      >
                        Issue Refund
                      </Button>
                    )}
                    {canManageOrders && (
                      <Button
                        onClick={() => {
                          setPaymentForm({
                            method: order.payment?.method || '',
                            status: normalizePaymentStatus(order.payment?.status),
                            syncToShopify: !!order.shopify_order_id
                          });
                          setPaymentModalOpen(true);
                        }}
                      >
                        Edit Payment
                      </Button>
                    )}
                  </ButtonGroup>
                </InlineStack>

                <Divider />

                <InlineStack align="space-between">
                  <Text variant="bodyMd" fontWeight="medium">Method:</Text>
                  <Text variant="bodyMd">{order.payment?.method || 'N/A'}</Text>
                </InlineStack>

                <InlineStack align="space-between">
                  <Text variant="bodyMd" fontWeight="medium">Status:</Text>
                  <Badge status={paymentDisplay.badgeStatus}>
                    {paymentDisplay.label}
                  </Badge>
                </InlineStack>

                <InlineStack align="space-between">
                  <Text variant="bodyMd" fontWeight="medium">Amount:</Text>
                  <Text variant="bodyMd" fontWeight="medium">
                    {new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: order.payment?.currency || 'EGP'
                    }).format(order.payment?.amount || 0)}
                  </Text>
                </InlineStack>

                <PaymentBalanceSection
                  order={order}
                  canManageOrders={canManageOrders}
                  onRecordPayment={() => setRecordPaymentOpen(true)}
                />

                {Number(order.payment?.refunded_amount || 0) > 0 && (
                  <>
                    <InlineStack align="space-between">
                      <Text variant="bodyMd" fontWeight="medium">Refunded:</Text>
                      <Text variant="bodyMd">
                        {new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: order.payment?.currency || 'EGP'
                        }).format(order.payment.refunded_amount)}
                      </Text>
                    </InlineStack>
                    <InlineStack align="space-between">
                      <Text variant="bodyMd" fontWeight="medium">Remaining refundable:</Text>
                      <Text variant="bodyMd">
                        {new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: order.payment?.currency || 'EGP'
                        }).format(remainingRefundable)}
                      </Text>
                    </InlineStack>
                  </>
                )}

                {order.payment?.transaction_id && (
                  <InlineStack align="space-between">
                    <Text variant="bodyMd" fontWeight="medium">Transaction ID:</Text>
                    <Text variant="bodyMd">{order.payment.transaction_id}</Text>
                  </InlineStack>
                )}

                {order.shopify_order_id && (
                  <InlineStack align="space-between">
                    <Text variant="bodyMd" fontWeight="medium">Shopify Order ID:</Text>
                    <Text variant="bodyMd">{order.shopify_order_id}</Text>
                  </InlineStack>
                )}

                {refunds.length > 0 && (
                  <>
                    <Divider />
                    <Text variant="headingSm" as="h3">Refund history</Text>
                    <DataTable
                      columnContentTypes={['text', 'numeric', 'text', 'text', 'text', 'text']}
                      headings={['Date', 'Amount', 'Method', 'Reason', 'Reference', 'Recorded by']}
                      rows={refunds.map(refund => [
                        refund.refund_date
                          ? new Date(refund.refund_date).toLocaleDateString()
                          : '-',
                        new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: refund.currency || order.payment?.currency || 'EGP'
                        }).format(refund.amount || 0),
                        refund.method || '-',
                        refund.reason || '-',
                        refund.reference || '-',
                        refund.created_by_name || refund.created_by || '-'
                      ])}
                    />
                  </>
                )}
              </BlockStack>
            </Card>
          </div>
        </Layout.Section>

        {/* Order Items */}
        <Layout.Section>
          <Card sectioned>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center">
                <Text variant="headingMd" as="h2">Order Items</Text>
                <InlineStack gap="200">
                {!editingOrder && (
                  <EditOrderButton
                    order={order}
                    canManageOrders={canManageOrders}
                    generationIssue={orderGenerationIssue}
                    onOpen={handleOpenEditMode}
                  />
                )}
                </InlineStack>
              </InlineStack>
              {editingOrder ? (
                <OrderEditMode
                  order={order}
                  catalog={storyCatalog.catalog}
                  capabilities={getEditCapabilities(order, { generationIssue: orderGenerationIssue })}
                  onExit={() => setEditingOrder(false)}
                  onSaved={handleOrderEditSaved}
                  onReload={handleReloadOrder}
                />
              ) : itemsTableRows.length > 0 ? (
                <DataTable
                  columnContentTypes={['text', 'text', 'text', 'text', 'text', 'numeric', 'text']}
                  headings={itemsTableHeadings}
                  rows={itemsTableRows}
                />
              ) : (
                <Text variant="bodyMd" color="subdued">No items found</Text>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Flags */}
        {order.flags && order.flags.length > 0 && (() => {
          const unresolvedFlags = order.flags.filter(flag => !flag.resolved);
          const resolvedFlags = order.flags.filter(flag => flag.resolved);

          return (
            <>
              {/* Unresolved Flags */}
              {unresolvedFlags.length > 0 && (
                <Layout.Section>
                  <Card title="Flags" sectioned>
                    <BlockStack gap="200">
                      {unresolvedFlags.map((flag, index) => (
                        <div key={index} style={{ position: 'relative' }}>
                          <Banner
                            status={FLAG_TYPE_COLORS[flag.flag_type] || 'warning'}
                            title={FLAG_TYPE_LABELS[flag.flag_type] || flag.flag_type}
                          >
                            <BlockStack gap="100">
                              <p>{flag.description}</p>
                              <Text variant="bodySm" color="subdued">
                                Added by {flag.user_name || 'System'} on{' '}
                                {new Date(flag.timestamp).toLocaleDateString()}
                              </Text>
                            </BlockStack>
                          </Banner>
                          <div style={{ position: 'absolute', top: '16px', right: '16px' }}>
                            <Button
                              size="slim"
                              onClick={() => openResolveFlagModal(flag)}
                            >
                              Mark as Resolved
                            </Button>
                          </div>
                        </div>
                      ))}
                    </BlockStack>
                  </Card>
                </Layout.Section>
              )}

              {/* Resolved Flags */}
              {resolvedFlags.length > 0 && (
                <Layout.Section>
                  <Card sectioned>
                    <BlockStack gap="300">
                      <InlineStack align="space-between">
                        <Text variant="headingMd">Resolved Flags ({resolvedFlags.length})</Text>
                        <Button
                          plain
                          onClick={() => setShowResolvedFlags(!showResolvedFlags)}
                        >
                          {showResolvedFlags ? 'Hide' : 'Show'}
                        </Button>
                      </InlineStack>

                      {showResolvedFlags && (
                        <BlockStack gap="200">
                          {resolvedFlags.map((flag, index) => (
                            <Banner
                              key={index}
                              status="info"
                              title={`${FLAG_TYPE_LABELS[flag.flag_type] || flag.flag_type} (Resolved)`}
                            >
                              <BlockStack gap="150">
                                <p>{flag.description}</p>
                                <Text variant="bodySm" color="subdued">
                                  Added by {flag.user_name || 'System'} on{' '}
                                  {new Date(flag.timestamp).toLocaleDateString()}
                                </Text>
                                <Divider />
                                <Text variant="bodySm" fontWeight="semibold" color="success">
                                  Resolved by {flag.resolved_by || 'System'} on{' '}
                                  {new Date(flag.resolved_at).toLocaleDateString()}
                                </Text>
                                {flag.resolution_notes && (
                                  <Text variant="bodySm" color="subdued">
                                    Notes: {flag.resolution_notes}
                                  </Text>
                                )}
                              </BlockStack>
                            </Banner>
                          ))}
                        </BlockStack>
                      )}
                    </BlockStack>
                  </Card>
                </Layout.Section>
              )}
            </>
          );
        })()}

        {/* Comments */}
        {order.comments && order.comments.length > 0 && (
          <Layout.Section>
            <Card title="Comments" sectioned>
              <DataTable
                columnContentTypes={['text', 'text', 'text', 'text']}
                headings={commentsTableHeadings}
                rows={commentsTableRows}
              />
            </Card>
          </Layout.Section>
        )}
      </Layout>

      {/* Status Update Modal */}
      <Modal
        open={statusModalOpen}
        onClose={() => setStatusModalOpen(false)}
        title={<div style={{ padding: '16px' }}>{'Update Order Status'}</div>}
        primaryAction={{
          content: 'Update Status',
          onAction: handleStatusUpdate,
          loading: updating,
          disabled: !statusForm.status || statusForm.status === order.status
        }}
        secondaryActions={[{
          content: 'Cancel',
          onAction: () => setStatusModalOpen(false)
        }]}
      >
        <Modal.Section>
          <Form onSubmit={handleStatusUpdate}>
            <FormLayout>
              <Select
                label="New Status"
                options={[
                  { value: '', label: 'Select status' },
                  ...((authService.getUser()?.permissions.includes("manage_orders"))
                    ? Object.values(
                      order.source === 'offline' ? OFFLINE_ORDER_STATUS : ORDER_STATUS
                    ).map(status => ({
                      value: status,
                      label: ORDER_STATUS_LABELS[status]
                    }))
                    : nextStatuses.map(status => ({
                      value: status,
                      label: ORDER_STATUS_LABELS[status]
                    }))
                  )
                ]}
                value={statusForm.status}
                onChange={(value) => setStatusForm(prev => ({ ...prev, status: value }))}
              />
            </FormLayout>
          </Form>
        </Modal.Section>
      </Modal>

      {/* Comment Modal */}
      <Modal
        open={commentModalOpen}
        onClose={() => setCommentModalOpen(false)}
        title={<div style={{ padding: '16px' }}>{'Add Comment'}</div>}
        primaryAction={{
          content: 'Add Comment',
          onAction: handleAddComment,
          loading: updating,
          disabled: !commentForm.text.trim()
        }}
        secondaryActions={[{
          content: 'Cancel',
          onAction: () => setCommentModalOpen(false)
        }]}
      >
        <Modal.Section>
          <Form onSubmit={handleAddComment}>
            <FormLayout>
              <TextField
                label="Comment"
                value={commentForm.text}
                onChange={(value) => setCommentForm(prev => ({ ...prev, text: value }))}
                multiline={4}
                placeholder="Enter your comment"
              />

            </FormLayout>
          </Form>
        </Modal.Section>
      </Modal>

      {/* Flag Modal */}
      <Modal
        open={flagModalOpen}
        onClose={() => setFlagModalOpen(false)}
        title={<div style={{ padding: '16px' }}>{'Add Flag'}</div>}
        primaryAction={{
          content: 'Add Flag',
          onAction: handleAddFlag,
          loading: updating,
          disabled: !flagForm.type || !flagForm.description.trim()
        }}
        secondaryActions={[{
          content: 'Cancel',
          onAction: () => setFlagModalOpen(false)
        }]}
      >
        <Modal.Section>
          <Form onSubmit={handleAddFlag}>
            <FormLayout>
              <Select
                label="Flag Type"
                options={[
                  { value: '', label: 'Select flag type' },
                  ...Object.entries(FLAG_TYPE_LABELS).map(([value, label]) => ({
                    value,
                    label
                  }))
                ]}
                value={flagForm.type}
                onChange={(value) => setFlagForm(prev => ({ ...prev, type: value }))}
              />

              <TextField
                label="Description"
                value={flagForm.description}
                onChange={(value) => setFlagForm(prev => ({ ...prev, description: value }))}
                multiline={3}
                placeholder="Describe the issue or concern"
              />

              <Select
                label="Priority"
                options={[
                  { value: 'low', label: 'Low' },
                  { value: 'normal', label: 'Normal' },
                  { value: 'high', label: 'High' },
                  { value: 'urgent', label: 'Urgent' }
                ]}
                value={flagForm.priority}
                onChange={(value) => setFlagForm(prev => ({ ...prev, priority: value }))}
              />
            </FormLayout>
          </Form>
        </Modal.Section>
      </Modal>

      {/* Resolve Flag Modal */}
      <Modal
        open={resolveFlagModalOpen}
        onClose={() => setResolveFlagModalOpen(false)}
        title={<div style={{ padding: '16px' }}>{'Resolve Flag'}</div>}
        primaryAction={{
          content: 'Resolve',
          onAction: handleResolveFlag,
          loading: updating
        }}
        secondaryActions={[{
          content: 'Cancel',
          onAction: () => setResolveFlagModalOpen(false)
        }]}
      >
        <Modal.Section>
          {selectedFlag && (
            <BlockStack gap="300">
              <Banner status="info">
                <BlockStack gap="100">
                  <Text variant="bodyMd" fontWeight="semibold">
                    {FLAG_TYPE_LABELS[selectedFlag.flag_type] || selectedFlag.flag_type}
                  </Text>
                  <Text variant="bodySm">{selectedFlag.description}</Text>
                </BlockStack>
              </Banner>

              <TextField
                label="Resolution Notes (Optional)"
                value={resolutionNotes}
                onChange={setResolutionNotes}
                multiline={3}
                placeholder="Add notes about how this was resolved..."
                helpText="Optional: Provide details about the resolution"
              />
            </BlockStack>
          )}
        </Modal.Section>
      </Modal>

      {/* Image Preview Modal */}
      <Modal
        open={imagePreviewModalOpen}
        onClose={() => setImagePreviewModalOpen(false)}
        title={selectedImage.name || 'Kid Photo'}
        large
      >
        <Modal.Section>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
            backgroundColor: '#f6f6f7',
            borderRadius: '8px'
          }}>
            {selectedImage.url ? (
              <img
                src={selectedImage.url}
                alt={selectedImage.name}
                style={{
                  maxWidth: '100%',
                  maxHeight: '600px',
                  objectFit: 'contain',
                  borderRadius: '4px'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
            ) : null}
            <div style={{
              display: 'none',
              textAlign: 'center',
              color: '#6d7175'
            }}>
              <p>Unable to load image</p>
              <p style={{ fontSize: '14px', wordBreak: 'break-all' }}>
                {selectedImage.url}
              </p>
            </div>
          </div>
        </Modal.Section>
      </Modal>

      {/* Payment Update Modal */}
      <Modal
        open={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        title={<div style={{ padding: '16px' }}>{'Update Payment Information'}</div>}
        primaryAction={{
          content: discountPending ? 'Apply discount' : 'Update Payment',
          onAction: handleUpdatePayment,
          loading: updating,
          disabled: discountPending
            ? !discountReady
            : !paymentForm.method && !paymentForm.status
        }}
        secondaryActions={[{
          content: 'Cancel',
          onAction: () => setPaymentModalOpen(false)
        }]}
      >
        <Modal.Section>
          <Form onSubmit={handleUpdatePayment}>
            <FormLayout>
              <Select
                label="Payment Method"
                options={[
                  { value: '', label: 'Keep current' },
                  { value: 'cod', label: 'Cash on Delivery (COD)' },
                  { value: 'instapay', label: 'InstaPay' },
                  { value: 'mobile_wallet', label: 'Mobile Wallet' },
                  { value: 'online_payment', label: 'Online Payment' }
                ]}
                value={paymentForm.method}
                onChange={(value) => setPaymentForm(prev => ({ ...prev, method: value }))}
                helpText="Change the payment method for this order"
              />

              <Select
                label="Payment Status"
                options={[
                  { value: '', label: 'Keep current' },
                  { value: 'pending', label: 'Pending' },
                  { value: 'paid', label: 'Paid' }
                ]}
                value={paymentForm.status}
                onChange={(value) => setPaymentForm(prev => ({ ...prev, status: value }))}
                helpText="Update the payment status"
              />

              <PaymentDiscountFields
                order={order}
                discount={paymentDiscount}
                onChange={handleDiscountChange}
                capabilities={paymentCapabilities}
                preview={discountPreview}
                status={discountStatus}
                error={discountPreviewError}
                saveError={paymentDiscountError}
              />

              {order.shopify_order_id && (
                <Checkbox
                  label="Sync to Shopify"
                  checked={paymentForm.syncToShopify}
                  onChange={(value) => setPaymentForm(prev => ({ ...prev, syncToShopify: value }))}
                  helpText={`Update payment information in Shopify order ${order.shopify_order_id}`}
                />
              )}

              <Banner status="info">
                <p>
                  {!paymentForm.method && !paymentForm.status && !discountPending
                    && 'Select at least one field to update.'}
                  {discountPending && 'The discount is applied first, then any payment change.'}
                  {paymentForm.method && !paymentForm.status && `This will change the payment method to ${paymentForm.method}.`}
                  {!paymentForm.method && paymentForm.status && `This will change the payment status to ${paymentForm.status}.`}
                  {paymentForm.method && paymentForm.status && `This will update both payment method and status.`}
                  {paymentForm.syncToShopify && order.shopify_order_id && ' Changes will be synced to Shopify.'}
                </p>
              </Banner>
            </FormLayout>
          </Form>
        </Modal.Section>
      </Modal>

      <Modal
        open={refundModalOpen}
        onClose={() => !refundSubmitting && setRefundModalOpen(false)}
        title="Issue Refund"
        primaryAction={{
          content: 'Record Refund',
          onAction: handleIssueRefund,
          loading: refundSubmitting,
          disabled: !refundForm.amount
            || Number(refundForm.amount) <= 0
            || Number(refundForm.amount) > remainingRefundable
            || !refundForm.reason.trim()
            || !refundForm.method
        }}
        secondaryActions={[{
          content: 'Cancel',
          onAction: () => setRefundModalOpen(false),
          disabled: refundSubmitting
        }]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <Banner tone="warning">
              This records a refund in Snaps. It does not send money through Paymob, Bosta, Shopify, or a bank.
            </Banner>
            <InlineStack gap="400">
              <Text as="p">
                Paid: {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: order.payment?.currency || 'EGP'
                }).format(paymentAmount)}
              </Text>
              <Text as="p">
                Already refunded: {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: order.payment?.currency || 'EGP'
                }).format(refundedAmount)}
              </Text>
              <Text as="p">
                Remaining: {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: order.payment?.currency || 'EGP'
                }).format(remainingRefundable)}
              </Text>
            </InlineStack>
            <FormLayout>
              <InlineStack gap="300">
                <TextField
                  label="Refund amount"
                  type="number"
                  min={0.01}
                  max={remainingRefundable}
                  value={refundForm.amount}
                  onChange={(value) => setRefundForm(current => ({ ...current, amount: value }))}
                  prefix={order.payment?.currency || 'EGP'}
                  autoComplete="off"
                />
                <TextField
                  label="Refund date"
                  type="date"
                  value={refundForm.refund_date}
                  onChange={(value) => setRefundForm(current => ({ ...current, refund_date: value }))}
                  autoComplete="off"
                />
              </InlineStack>
              <Select
                label="Refund method"
                value={refundForm.method}
                onChange={(value) => setRefundForm(current => ({ ...current, method: value }))}
                options={[
                  { label: 'Select method', value: '' },
                  { label: 'Original payment method', value: 'original_payment' },
                  { label: 'Bank transfer', value: 'bank_transfer' },
                  { label: 'InstaPay', value: 'instapay' },
                  { label: 'Mobile wallet', value: 'mobile_wallet' },
                  { label: 'Cash', value: 'cash' },
                  { label: 'Other', value: 'other' }
                ]}
              />
              <TextField
                label="Reason"
                value={refundForm.reason}
                onChange={(value) => setRefundForm(current => ({ ...current, reason: value }))}
                autoComplete="off"
              />
              <TextField
                label="External reference"
                value={refundForm.reference}
                onChange={(value) => setRefundForm(current => ({ ...current, reference: value }))}
                autoComplete="off"
                helpText="Optional transfer or payment reference"
              />
              <TextField
                label="Notes"
                value={refundForm.notes}
                onChange={(value) => setRefundForm(current => ({ ...current, notes: value }))}
                multiline={3}
                autoComplete="off"
              />
            </FormLayout>
          </BlockStack>
        </Modal.Section>
      </Modal>

      <OrderItemEditModal {...orderItemEditModalProps} />

      <RecordPaymentModal
        isOpen={recordPaymentOpen}
        order={order}
        onClose={() => setRecordPaymentOpen(false)}
        onRecorded={() => {
          setToast({ content: 'Payment recorded', error: false });
          setRecordPaymentOpen(false);
          loadOrder();
        }}
      />

      {/* Add Addons Modal */}
      <Modal
        open={isAddonsModalOpen}
        onClose={() => setIsAddonsModalOpen(false)}
        title="Add Addon Services"
        primaryAction={{
          content: 'Apply Addons',
          loading: addonsLoading,
          onAction: async () => {
            try {
              setAddonsLoading(true);

              const payload = {};
              let hasChange = false;

              // Express — only send if toggling ON (wasn't already express)
              const wasExpress = order.line_items?.some(i => i.is_express);
              if (addonsForm.express && !wasExpress) {
                payload.express = true;
                hasChange = true;
              }

              // Gift Wrap — only send newly added IDs
              const existingGiftIds = (order.line_items || []).filter(i => i.is_gift).map(i => i.id);
              const newGiftIds = addonsForm.giftLineItemIds.filter(id => !existingGiftIds.includes(id));
              if (newGiftIds.length > 0) {
                payload.giftLineItemIds = newGiftIds;
                hasChange = true;
              }

              // Gift Notes — send new or changed notes
              const giftNotes = [];
              for (const [lineItemId, note] of Object.entries(addonsForm.giftNotes)) {
                if (note && note.trim()) {
                  giftNotes.push({ lineItemId, note: note.trim() });
                }
              }
              if (giftNotes.length > 0) {
                payload.giftNotes = giftNotes;
                hasChange = true;
              }

              // Price
              if (addonsForm.newPrice !== '' && addonsForm.newPrice !== undefined) {
                const price = parseFloat(addonsForm.newPrice);
                if (!isNaN(price) && price >= 0) {
                  payload.newPrice = price;
                  hasChange = true;
                }
              }

              if (!hasChange) {
                setToast({ content: 'No changes detected', error: false });
                setIsAddonsModalOpen(false);
                return;
              }

              const result = await ordersService.addOrderAddons(order.order_id, payload);
              if (result.success) {
                setToast({ content: `Addons applied: ${result.changes?.join('; ') || 'Success'}`, error: false });
                setIsAddonsModalOpen(false);
                loadOrder();
              } else {
                throw new Error(result.error || 'Failed');
              }
            } catch (error) {
              setToast({ content: `Error: ${error.message}`, error: true });
            } finally {
              setAddonsLoading(false);
            }
          }
        }}
        secondaryActions={[{ content: 'Cancel', onAction: () => setIsAddonsModalOpen(false) }]}
      >
        <Modal.Section>
          <FormLayout>
            {/* Express Delivery (order-level) */}
            <Checkbox
              label="⚡ Express Delivery (all items)"
              checked={addonsForm.express}
              onChange={(val) => setAddonsForm(prev => ({ ...prev, express: val }))}
              helpText="Express applies to the entire order since all items are delivered together"
              disabled={order?.line_items?.some(i => i.is_express)}
            />
            {order?.line_items?.some(i => i.is_express) && (
              <Banner status="info">
                <p>This order already has Express delivery enabled.</p>
              </Banner>
            )}

            {/* Gift Wrap (per item) */}
            <Text variant="headingSm" as="h3">🎁 Gift Wrap</Text>
            {visibleLineItems.map(item => (
              <Checkbox
                key={`gift-${item.id}`}
                label={item.name || item.title || item.sku || item.id}
                checked={addonsForm.giftLineItemIds.includes(item.id)}
                onChange={(val) => {
                  setAddonsForm(prev => ({
                    ...prev,
                    giftLineItemIds: val
                      ? [...prev.giftLineItemIds, item.id]
                      : prev.giftLineItemIds.filter(id => id !== item.id)
                  }));
                }}
                disabled={item.is_gift}
              />
            ))}

            {/* Gift Note (per item) */}
            <Text variant="headingSm" as="h3">📝 Gift Note</Text>
            {visibleLineItems.map(item => (
              <TextField
                key={`note-${item.id}`}
                label={item.name || item.title || item.sku || item.id}
                value={addonsForm.giftNotes[item.id] || ''}
                onChange={(val) => {
                  setAddonsForm(prev => ({
                    ...prev,
                    giftNotes: { ...prev.giftNotes, [item.id]: val }
                  }));
                }}
                placeholder="Enter gift note message..."
                multiline={2}
              />
            ))}

            {/* Manual Price Adjustment */}
            <Text variant="headingSm" as="h3">💰 Price Adjustment</Text>
            <TextField
              label="New Total Price"
              type="number"
              value={addonsForm.newPrice}
              onChange={(val) => setAddonsForm(prev => ({ ...prev, newPrice: val }))}
              prefix={order?.payment?.currency || 'EGP'}
              placeholder={order?.total_price || '0.00'}
              helpText={`Current price: ${order?.payment?.currency || 'EGP'} ${order?.total_price || '0.00'}. Leave empty to keep current price.`}
            />
          </FormLayout>
        </Modal.Section>
      </Modal>

      {/* View Generation Modal */}
      <ViewGenerationModal
        isOpen={isViewGenerationModalOpen}
        onClose={() => {
          setIsViewGenerationModalOpen(false);
          setSelectedGeneration(null);
          // Refresh order and generations after modal closes
          loadOrder();
        }}
        selectedGeneration={selectedGeneration}
        setToastMessage={(message) => setToast({ content: message, error: false })}
        setToastActive={(active) => {
          if (!active) setToast(null);
        }}
      />

      {/* Kid Image Selection Modal */}
      <KidImageSelectionModal
        isOpen={isKidImageSelectionModalOpen}
        onClose={() => {
          setIsKidImageSelectionModalOpen(false);
          setSelectedGenerationForKidImages(null);
        }}
        generation={selectedGenerationForKidImages}
        setToastMessage={(message) => setToast({ content: message, error: false })}
        setToastActive={(active) => {
          if (!active) setToast(null);
        }}
        onGenerationUpdated={() => loadOrder()}
      />

      {/* Add Kid Photos Modal */}
      <AddKidPhotosModal
        isOpen={isAddKidPhotosModalOpen}
        onClose={() => {
          setIsAddKidPhotosModalOpen(false);
          setSelectedLineItemForPhotos(null);
        }}
        orderId={orderId}
        lineItemId={selectedLineItemForPhotos?.id}
        currentPhotos={extractKidPhotos(selectedLineItemForPhotos?.properties)}
        setToastMessage={(message) => setToast({ content: message, error: false })}
        setToastActive={(active) => {
          if (!active) setToast(null);
        }}
        onPhotosAdded={handlePhotosAdded}
      />

      {/* Merge Order Modal */}
      <Modal
        open={mergeModalOpen}
        onClose={() => setMergeModalOpen(false)}
        title={<div style={{ padding: '16px' }}>Merge Orders</div>}
        primaryAction={{
          content: 'Merge & Cancel Source',
          onAction: handleMergeOrders,
          loading: updating,
          destructive: true,
          disabled: !mergeForm.sourceOrderId
        }}
        secondaryActions={[{
          content: 'Cancel',
          onAction: () => setMergeModalOpen(false)
        }]}
      >
        <Modal.Section>
          <Form onSubmit={handleMergeOrders}>
            <FormLayout>
              <Banner status="critical">
                <p>
                  <strong>Warning: Irreversible Action</strong><br />
                  Merging will move all items from the Source Order to this order ({order?.order_id}).
                  The Source Order will be automatically <strong>CANCELLED</strong>.
                </p>
              </Banner>

              <TextField
                label="Source Order ID"
                value={mergeForm.sourceOrderId}
                onChange={(value) => setMergeForm(prev => ({ ...prev, sourceOrderId: value }))}
                placeholder="e.g. ORD-1234-5678"
                helpText="Enter the ID of the order you want to merge INTO this one."
                autoComplete="off"
              />
            </FormLayout>
          </Form>
        </Modal.Section>
      </Modal>

      {/* Merge Order Modal */}
      <Modal
        open={mergeModalOpen}
        onClose={() => setMergeModalOpen(false)}
        title={<div style={{ padding: '16px' }}>Merge Orders</div>}
        primaryAction={{
          content: 'Merge & Cancel Source',
          onAction: handleMergeOrders,
          loading: updating,
          destructive: true,
          disabled: !mergeForm.sourceOrderId
        }}
        secondaryActions={[{
          content: 'Cancel',
          onAction: () => setMergeModalOpen(false)
        }]}
      >
        <Modal.Section>
          <Form onSubmit={handleMergeOrders}>
            <FormLayout>
              <Banner status="critical">
                <p>
                  <strong>Warning: Irreversible Action</strong><br />
                  Merging will move all items from the Source Order to this order ({order?.order_id}).
                  The Source Order will be automatically <strong>CANCELLED</strong>.
                </p>
              </Banner>

              <TextField
                label="Source Order ID"
                value={mergeForm.sourceOrderId}
                onChange={(value) => setMergeForm(prev => ({ ...prev, sourceOrderId: value }))}
                placeholder="e.g. ORD-1234-5678"
                helpText="Enter the ID of the order you want to merge INTO this one."
                autoComplete="off"
              />
            </FormLayout>
          </Form>
        </Modal.Section>
      </Modal>

      {toast && (
        <Toast
          content={toast.content}
          error={toast.error}
          onDismiss={() => setToast(null)}
        />
      )}
      {/* Edit Shipping Address Modal */}
      <EditShippingAddressModal
        isOpen={isEditShippingAddressModalOpen}
        onClose={() => setIsEditShippingAddressModalOpen(false)}
        order={order}
        onOrderUpdated={loadOrder}
        setToastMessage={(msg) => setToast({ content: msg })}
        setToastActive={(active) => !active && setToast(null)}
      />
    </>

  );
};

export default OrderDetailPage;
